/**
 * usePosts Hook
 *
 * Custom hook for fetching and managing posts from Supabase with polling.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { fetchPosts } from "../api/supabase";

/**
 * Hook for fetching posts by status with automatic polling
 * @param {string} status - Status to filter by
 * @param {number} pollInterval - Polling interval in ms (default: 10000)
 * @returns {{posts: Array, loading: boolean, error: string|null, refetch: Function}}
 */
export const usePosts = (status, pollInterval = 10000) => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [backoffMultiplier, setBackoffMultiplier] = useState(1);

  const timeoutRef = useRef(null);
  const isMountedRef = useRef(true);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await fetchPosts(status);

      if (isMountedRef.current) {
        setPosts(data);
        setBackoffMultiplier(1); // Reset backoff on success
      }
    } catch (err) {
      console.error("Error fetching posts:", err);
      if (isMountedRef.current) {
        setError(err.message);
        // Increase backoff on error
        setBackoffMultiplier((prev) => Math.min(prev + 1, 5));
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [status]);

  const refetch = useCallback(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    isMountedRef.current = true;

    // Initial fetch
    fetchData();

    // Set up polling with progressive backoff
    const schedulePoll = () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      const interval = pollInterval * backoffMultiplier;
      timeoutRef.current = setTimeout(() => {
        fetchData();
        schedulePoll();
      }, interval);
    };

    schedulePoll();

    // Cleanup
    return () => {
      isMountedRef.current = false;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [status, pollInterval, backoffMultiplier, fetchData]);

  return {
    posts,
    loading,
    error,
    refetch,
  };
};

export default usePosts;
